class DoubleRangeSliderModule extends WiroModule {
  constructor(parentEl) {
    super("doubleRangeSlider", parentEl);
    this.rangeMin = 0;
    this.rangeInputs = [...parentEl.querySelectorAll(".range-input input")];
    this.rangePrices = [...parentEl.querySelectorAll(".range-price input")];
    this.activeBackground = parentEl.querySelector(".range-active-background");
  }

  get minBase() {
    return this.rangeInputs.find((input) => "min" in input.dataset).min;
  }
  get maxBase() {
    return this.rangeInputs.find((input) => "max" in input.dataset).max;
  }
  get min() {
    return this.rangeInputs.find((input) => "min" in input.dataset).value;
  }
  get max() {
    return this.rangeInputs.find((input) => "max" in input.dataset).value;
  }
  get minPercentage() {
    return (this.min / this.rangeInputs[1].max) * 100;
  }
  get maxPercentage() {
    return (this.max / this.rangeInputs[1].max) * 100;
  }

  bindEvents() {
    this.rangeHandler = this.parentEl.modules.requestUtility.debounce(this.handleRange.bind(this));
    this.liveRangeHandler = this.handleLiveRange.bind(this);
    this.rangeInputs.forEach((input) => {
      input.addEventListener("input", this.rangeHandler);
      input.addEventListener("input", this.liveRangeHandler);
    });
    this.inputHandler = this.parentEl.modules.requestUtility.debounce(this.handleInput.bind(this));
    this.rangePrices.forEach((input) => {
      input.addEventListener("input", this.inputHandler);
    });
  }

  setRangeMax(max) {
    this.rangePrices[1].max = max;
    if (this.rangePrices[1].value > max) this.rangePrices[1].value = max;
    this.rangeInputs[1].max = max;
    if (this.rangeInputs[1].value > max) this.rangeInputs[1].value = max;
    this.rangePrices[0].max = max;
    if (this.rangePrices[0].value > max) this.rangePrices[0].value = max;
    this.rangeInputs[0].max = max;
    if (this.rangeInputs[0].value > max) this.rangeInputs[0].value = max;
    this.handleLiveRange();
  }

  handleLiveRange(e) {
    this.activeBackground.style.left = `${this.minPercentage}%`;
    this.activeBackground.style.width = `${this.maxPercentage - this.minPercentage}%`;
    this.rangeInputs[0].value = this.min;
    this.rangeInputs[1].value = this.max;
  }

  handleInput(e) {
    let minPrice = this.rangePrices[0].value;
    let maxPrice = this.rangePrices[1].value;
    if (maxPrice - minPrice >= this.rangeMin && maxPrice <= this.rangeInputs[1].max) {
      if ("min" in e.target.dataset) {
        this.rangeInputs[0].value = minPrice;
      } else {
        this.rangeInputs[1].value = maxPrice;
      }
    }
    this.parentEl.performUpdate();
  }

  handleRange(e) {
    let minRange = parseInt(this.rangeInputs[0].value);
    let maxRange = parseInt(this.rangeInputs[1].value);
    if (maxRange - minRange < this.rangeMin) {
      if ("min" in e.target.dataset) {
        this.rangeInputs[0].value = maxRange - this.rangeMin;
      } else {
        this.rangeInputs[1].value = minRange + this.rangeMin;
      }
    } else {
      this.rangePrices[0].value = minRange;
      this.rangePrices[1].value = maxRange;
    }
    this.parentEl.performUpdate();
  }

  unbindEvents() {
    this.rangeInputs.forEach((input) => {
      input.removeEventListener("input", this.rangeHandler);
      input.removeEventListener("input", this.liveRangeHandler);
    });
    this.rangePrices.forEach((input) => {
      input.removeEventListener("input", this.inputHandler);
    });
  }
}

class SwatchModule extends WiroModule {
  constructor(parentEl) {
    super("swatch", parentEl);
  }

  getSwatch(colour) {
    const swatches = {
      black: "#000000",
      red: "#ff0000",
      blue: "#006FCF",
      green: "#009951",
      yellow: "#E5A000",
    };
    if (window.filterSwatches)
      Object.entries(window.filterSwatches).forEach(([colour, hex]) => (swatches[colour] = hex));
    return swatches[colour.toLowerCase()];
  }
}

class MobileSlideOutModule extends WiroModule {
  constructor(parentEl) {
    super("mobileSlideOut", parentEl);
  }

  get toggles() {
    return [...this.parentEl.querySelectorAll("[data-mobile-slide-out-toggle]")];
  }

  get slideOuts() {
    return [...this.parentEl.querySelectorAll("[data-mobile-slide-out]")];
  }

  bindEvents() {
    this.clickHandler = this.handleClick.bind(this);
    this.toggles.forEach((toggle) => toggle.addEventListener("click", this.clickHandler));
  }

  unbindEvents() {}

  handleClick(e) {
    let target = e.target;
    if (target.tagName === "IMG" && target.closest("button")) target = e.target.closest("button");
    const slideOutName = target.dataset.mobileSlideOutToggle;
    const matchingSlideOut = this.slideOuts.find((slideOut) => slideOut.dataset.mobileSlideOut === slideOutName);
    if (!matchingSlideOut) return;
    matchingSlideOut.toggleAttribute("open");
    target.toggleAttribute("open", matchingSlideOut.hasAttribute("open"));
    const parentZIndexTarget = target.closest("[data-bump-z-index-on-child]");
    if (parentZIndexTarget) parentZIndexTarget.classList.toggle("tw-z-[9999]", matchingSlideOut.hasAttribute("open"));
  }
}

class SearchspringCollection extends WiroHTMLRenderer {
  constructor() {
    super([SearchspringAPIModule, DoubleRangeSliderModule, RequestUtilityModule, MobileSlideOutModule, SwatchModule]);
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.performUpdate(false, true);
    this.clickHandler = this.handleClick.bind(this);
    this.addEventListener("click", this.clickHandler);
    if (this.searchInput) {
      this.searchInputHandler = this.modules.requestUtility.debounce(this.handleSearchInput.bind(this));
      this.searchInput.addEventListener("input", this.searchInputHandler);
    }
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.clickOutsideHandler);
    this.previousSize = window.innerWidth;
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener("resize", this.resizeHandler, { passive: true });

    this.desktopThreshold = 750;
    if (window.innerWidth >= this.desktopThreshold) {
      this.updateDesktop();
    } else {
      this.updateMobile();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.clickHandler);
    if (this.searchInput) {
      this.searchInput.removeEventListener("input", this.searchInputHandler);
    }
    document.removeEventListener("click", this.clickOutsideHandler);
    window.removeEventListener("resize", this.resizeHandler);
  }

  get collection() {
    if (this.dataset.collection) return this.dataset.collection;
    return window.location.href.split("/collections/")?.[1];
  }

  get filtersContainer() {
    return this.querySelector("[data-collection-filters]");
  }

  get searchText() {
    return this.querySelector("[data-search-text]");
  }

  get searchQuery() {
    return this.querySelector("[data-search-query]");
  }

  get selectedFiltersContainer() {
    return this.querySelector("[data-selected-filters]");
  }

  get sortContainer() {
    return this.querySelector("[data-collection-sort]");
  }

  get paginationContainer() {
    return this.querySelector("[data-collection-pagination]");
  }

  get filterProxies() {
    return [...this.querySelectorAll("[data-filter-proxy-for]")];
  }

  get productContainer() {
    return this.querySelector("[data-collection-grid]");
  }

  get emptyStateContainer() {
    return this.querySelector("[data-empty-state-container]");
  }

  get filtersToShow() {
    return ["ss_full_path", "vendor", "mfield_tabledetails_main_color", "mfield_tabledetails_silhouette"];
  }

  get queryParams() {
    const searchParam = new URLSearchParams(location.search);
    return [...searchParam].map(([key, value]) => ({ key, value }));
  }
  set queryParams(value) {
    if (history.pushState) {
      let queryParams = "";
      if (value.length) {
        queryParams = `?${this.modules.searchspring.toQueryParams(value)}`;
      }
      const path = `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryParams}`;
      window.history.pushState({ path }, "", path);
    }
  }

  get filterElements() {
    return [...this.querySelectorAll("[data-filter-field]")];
  }

  get searchInput() {
    return this.querySelector("[data-search]");
  }

  get resultsCounterContainers() {
    return [...this.querySelectorAll("[data-results-counter]")];
  }

  get sortElements() {
    return [...this.querySelectorAll("[data-sort-field]")];
  }

  get filterAccordions() {
    return [...this.querySelectorAll("[data-filter-accordion]")];
  }

  get resetFiltersButton() {
    return this.querySelector("[data-reset-filters]");
  }

  get shouldSkipVariantFilters() {
    return this.dataset.skipVariantFilters === "true";
  }
  
  get checkedFilters() {
    const appliedFilters = this.filterElements.filter((el) => !el.hasAttribute("data-variant-filter") && el.checked);
    let checkedFiltersArray = [];
    for (const filter of appliedFilters) {
      checkedFiltersArray.push({ key: filter.dataset.filterField, value: filter.value });
    }
    return checkedFiltersArray;
  }

  get filterBar() {
    return this.querySelector("[data-filter-bar]");
  }

  get checkedSort() {
    const appliedSort = this.querySelector("[data-sort-field]:checked");
    if (!appliedSort) return null;
    return { key: appliedSort.dataset.sortField, value: appliedSort.value };
  }

  handleResize() {
    const isDesktop = window.innerWidth >= this.desktopThreshold;
    const wasDesktop = this.previousSize >= this.desktopThreshold;
    if (isDesktop && !wasDesktop) {
      this.updateDesktop();
    } else if (!isDesktop && wasDesktop) {
      this.updateMobile();
    }
    this.previousSize = window.innerWidth;
  }

  async handleSearchInput() {
    this.updateSearch();
    await this.performUpdate();
  }

  setInitialValues() {
    this.queryParams.forEach((param) => {
      const { key: k, value: v } = param;
      let type = "other";
      if (k.includes("sort")) type = "sort";
      if (k.includes("filter")) type = "filter";
      if (k.includes("page")) type = "page";
      if (k.includes("q")) type = "search";
      switch (type) {
        case "search":
          this.searchInput.value = v;
          break;
        case "filter":
          if (k.includes("price")) {
            if (k.includes("low")) {
              const matchingFilter = this.querySelector("[data-min]");
              if (matchingFilter) matchingFilter.value = v;
              const matchingFilterField = this.querySelector('[name="min"]');
              if (matchingFilterField) matchingFilterField.value = v;
            }
            if (k.includes("high")) {
              const matchingFilter = this.querySelector("[data-max]");
              if (matchingFilter) matchingFilter.value = v;
              const matchingFilterField = this.querySelector('[name="max"]');
              if (matchingFilterField) matchingFilterField.value = v;
            }
            this.modules.doubleRangeSlider.handleLiveRange();
            return;
          }
          const matchingFilter = this.filterElements.find((el) => {
            if (el.dataset.filterField !== k) return false;
            if (el.hasAttribute("data-variant-filter")) {
              if (!v.includes(el.value)) return false;
            } else {
              if (el.value !== v) return false;
            }
            return true;
          });
          if (!matchingFilter) return;
          matchingFilter.checked = true;
          break;
        case "sort":
          const matchingSort = this.sortElements.find((el) => el.dataset.sortField === k && el.value === v);
          if (!matchingSort) return;
          matchingSort.checked = true;
          break;
      }
    });
  }

  get allDetails() {
    return this.querySelectorAll("details");
  }

  get isDesktop() {
    return window.matchMedia("(min-width: 700px)");
  }

  handleClickOutside(e) {
    const { target } = e;
    if (target.closest("details")) return;
    if (!this.isDesktop) return;
    this.allDetails.forEach((details) => details.removeAttribute("open"));
  }

  async handleClick(e) {
    setTimeout(async () => {
      const { target } = e;
      let input = target;
      if (input.tagName === "LABEL") input = target.querySelector("input");
      if (input.tagName !== "INPUT" && target.closest("button")) input = target.closest("button");
      if (input.dataset.filterField) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        await this.performUpdate();
      } else if (input.dataset.sortField) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        await this.performUpdate();
      } else if (input.dataset.load) {
        this.queryParams = [
          ...this.queryParams.filter((q) => q.key !== "page"),
          { key: "page", value: input.dataset.load },
        ];

        await this.performUpdate(true);
      } else if (input.dataset.removeFilter) {
        const isVariant = input.hasAttribute("data-remove-variant-filter");
        if (isVariant) {
          this.removeVariantFilter(input.dataset.removeFilter);
        } else {
          this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
          const matchingFilter = this.filterElements.find((filter) => filter.value === input.dataset.removeFilter);
          matchingFilter.checked = false;
        }
        await this.performUpdate();
      } else if (input.hasAttribute("data-clear-filters")) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        this.filterElements.forEach((filter) => (filter.checked = false));
        this.queryParams = this.queryParams.filter((q) => !q.key.includes("filter"));
        this.modules.doubleRangeSlider.rangeInputs[0].value = this.modules.doubleRangeSlider.rangeInputs[0].min;
        this.modules.doubleRangeSlider.rangeInputs[1].value = this.modules.doubleRangeSlider.rangeInputs[1].max;
        this.modules.doubleRangeSlider.rangePrices[0].value = this.modules.doubleRangeSlider.rangeInputs[0].min;
        this.modules.doubleRangeSlider.rangePrices[1].value = this.modules.doubleRangeSlider.rangeInputs[1].max;
        this.modules.doubleRangeSlider.liveRangeHandler();
        await this.performUpdate();
      } else if (input.hasAttribute("data-filter-proxy-for")) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        const filterLabel = input.dataset.filterProxyFor;
        const matchingFilter = this.filterElements.find((filterEl) => {
          return filterEl.dataset.filterLabel === filterLabel;
        });
        matchingFilter.click();
      } else if (input.hasAttribute("data-up-hierarchy")) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        const field = input.dataset.upHierarchy;
        const delimiter = ">";
        const matchingElementInArray = this.queryParams.find(({ key, value }) => key === `filter.${field}`);
        if (!matchingElementInArray.value.includes(delimiter)) {
          this.queryParams = this.queryParams.filter(({ key, value }) => key !== `filter.${field}`);
        } else {
          this.queryParams = this.queryParams.map(({ key, value }) => {
            if (key !== `filter.${field}`) return { key, value };
            return { key, value: value.split(delimiter).slice(0, -1).join(delimiter) };
          });
        }
        await this.performUpdate();
      } else if (input.hasAttribute("data-reset-price")) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        const priceToReset = input.dataset.resetPrice;
        this.queryParams = this.queryParams.filter(
          (queryParam) => !queryParam.key.includes(`price.${priceToReset === "max" ? "high" : "low"}`),
        );
        const index = priceToReset === "max" ? 1 : 0;
        const rangeSlider = this.modules.doubleRangeSlider.rangeInputs[index];
        const rangeInput = this.modules.doubleRangeSlider.rangePrices[index];
        rangeSlider.value = rangeSlider[priceToReset];
        rangeInput.value = rangeSlider[priceToReset];
        this.modules.doubleRangeSlider.handleLiveRange();
        await this.performUpdate();
      } else if (input.hasAttribute("data-remove-filter-hierarchical")) {
        this.queryParams = [...this.queryParams.filter((q) => q.key !== "page")];
        const sectionToRemove = input.dataset.removeFilterHierarchical;
        const queryParam = this.queryParams.find((q) => q.key.includes("full_path"));
        const queryParamValue = queryParam.value.split(sectionToRemove)[0];
        if (queryParamValue.length) {
          this.queryParams = this.queryParams.map((q) => {
            if (!q.key.includes("full_path")) return q;
            return {
              key: q.key,
              value: queryParamValue.endsWith(">") ? queryParamValue.slice(0, -1) : queryParamValue,
            };
          });
        } else {
          this.queryParams = this.queryParams.filter((q) => !q.key.includes("full_path"));
        }
        await this.performUpdate();
      }
    }, 0);
  }

  async performUpdate(concat = false, initial = false) {
    const openFilters = [...this.querySelectorAll("[data-details][open]")].map((details) => details.dataset.details);
    this.enableLoadingState();
    const data = await this.requestData(initial);
    const inlineBanners = data.merchandising?.content?.inline || [];
    this.updateSort(data.sorting);
    this.updateFilters(data.facets);
    this.updateGrid(data.results, concat, inlineBanners);
    this.updatePagination(data.pagination);

    if (initial) {
      const priceFacet = data.facets.find((f) => f.field === "ss_price");
      if (priceFacet) this.modules.doubleRangeSlider.setRangeMax(priceFacet.range[1]);
      this.setInitialValues();
    }

    this.updateSearch();

    this.updateSelectedFilters(data.facets);
    this.updateResultsCounters(data.pagination);
    this.updateResetFiltersButton();
    setTimeout(() => {
      this.updateFilters(data.facets, true);
      this.updateQuickFilters();
      this.disableLoadingState();
      if (window.innerWidth >= this.desktopThreshold) {
        this.updateDesktop();
      } else {
        this.updateMobile();
      }
      openFilters.forEach((openFilter) => {
        const details = this.querySelector(`[data-details="${openFilter}"]`);
        if (details) details.open = true;
      });
    }, 0);
  }

  async requestData(initial) {
    let data;
    if (initial) {
      data = [...this.queryParams, ...this.getVariantFilterValue()];
    } else {
      data = [...this.checkedFilters, ...this.getVariantFilterValue()];
      if (this.searchInput) {
        if (this.searchInput.value.length) {
          data.push({ key: "q", value: this.searchInput.value });
        }
      }
      const hierarchicalFilter = this.queryParams.find((q) => q.key.includes("full_path"));
      if (hierarchicalFilter) data.push(hierarchicalFilter);
      if (this.checkedSort) data.push(this.checkedSort);
      const pageParam = this.queryParams.find((p) => p.key === "page");
      if (pageParam) data.push(pageParam);
      const minPrice = this.modules.doubleRangeSlider.min;
      const maxPrice = this.modules.doubleRangeSlider.max;
      data.push({ key: "filter.ss_price.low", value: minPrice });
      data.push({ key: "filter.ss_price.high", value: maxPrice });
      this.queryParams = data;
    }
    if (this.dataset.collection) {
      data.push({ key: "bgfilter.collection_handle", value: this.dataset.collection });
    }
    const response = await this.modules.searchspring.sendSearchRequest(data);
    // Check for merchandising redirect
    if (response?.merchandising?.redirect) {
      const redirectUrl = response.merchandising.redirect;
      // Redirect to the URL specified by Searchspring
      window.location.href = redirectUrl;
      return {
        pagination: { totalResults: 0, currentPage: 1, totalPages: 0 },
        sorting: { options: [] },
        facets: [],
        results: [],
      };
    }
    return response;
  }

  updateSearch() {
    if (!this.searchInput) return;
    this.searchQuery.innerText = this.searchInput.value;
    this.searchText.classList.toggle("tw-hidden", !this.searchInput.value.length);
  }

  parseBannerData(inlineBanners) {
    return inlineBanners
      .map((banner) => {
        const { value, config } = banner;
        const index = config?.position?.index || 8;

        const temp = document.createElement("div");
        temp.innerHTML = value;

        const script = temp.querySelector("script[data-banner-type]");

        if (!script) {
          return null;
        }

        return {
          index,
          id: script.dataset.bannerId,
          imageUrl: script.dataset.imageUrl || null,
          targetUrl: script.dataset.targetUrl || null,
          title: script.dataset.targetTitle || "",
        };
      })
      .filter(Boolean);
  }

  // DOM update methods
  updateSort(sort) {
    const dom = this.prepareStringForRender(`
      <details data-details="sort" class="tw-relative">
        <summary class="tw-flex tw-items-center tw-uppercase tw-font-semibold tw-text-[14px] sm:tw-text-2xl tw-gap-2 tw-p-4 sm:tw-px-6">
          ${"Sort"}
          <img src="${window.collectionIcons.sort}" alt="Open sort">
        </summary>
        <ul class="tw-absolute tw-bg-scheme-background-blur tw-backdrop-blur-lg tw-w-[248px] tw-right-0 tw-rounded-[4px]">
          ${sort.options
            .map((s) => {
              const { field, direction, label } = s;
              return `
              <li>
                <label class="tw-block tw-w-full hover:tw-bg-white tw-px-4 tw-py-3 tw-cursor-pointer">
                  <input class="tw-hidden tw-peer" data-sort-field="sort.${field}" value="${direction}" type="radio" name="sort" ${this.checkedSort && this.checkedSort.key === `sort.${field}` && this.checkedSort.value === direction ? "checked" : ""}>
                  <span class="peer-checked:tw-font-semibold">${label}</span>
                </label>
              </li>
            `;
            })
            .join("")}
        </ul>
      </details>
    `);
    this.sortContainer.innerHTML = "";
    this.sortContainer.appendChild(dom);
  }

  get variantFilters() {
    return [...this.querySelectorAll("[data-variant-filter]")];
  }

  isVariantFilterActive(value) {
    if (this.variantFilterValueCache) return this.variantFilterValueCache.some((vf) => vf.value.includes(`${value}`));
    return this.getVariantFilterValue().some((vf) => vf.value.includes(`${value}`));
  }

  removeVariantFilter(value) {
    const matchingFilterEl = this.variantFilters.find((f) => f.value === value);
    matchingFilterEl.checked = false;
  }

  getVariantFilterDisplayValue(v) {
    const [key, value] = v.split(":");
    if (key.includes("onsale")) {
      return value === "yes" ? "On Sale" : "Not On Sale";
    }
    return value;
  }

  updateSelectedFilters(filters) {
    let selectedVariantFilters = [];
    this.getVariantFilterValue().forEach((vf) => {
      const variantFilterValue = vf.value;
      selectedVariantFilters = [
        ...selectedVariantFilters,
        ...variantFilterValue
          .split("|")
          .filter((v) => !v.includes("*") && !v.includes("instock:yes"))
          .map((v) => {
            return {
              key: v.split(":")[0],
              value: v,
              displayValue: this.getVariantFilterDisplayValue(v),
              variantFilter: true,
            };
          }),
      ];
    });
    selectedVariantFilters = selectedVariantFilters.filter(
      (obj, index, arr) => arr.findIndex((item) => item.displayValue === obj.displayValue) === index,
    );

    this.selectedFiltersContainer.innerHTML = "";
    let totalFilters = this.checkedFilters.length + selectedVariantFilters.length;
    [...this.checkedFilters, ...selectedVariantFilters].forEach((filter) => {
      let value = filter.value;
      let displayValue = filter.displayValue || value;
      const dom = this.prepareStringForRender(`
        <button data-remove-filter="${filter.value}" ${filter.variantFilter ? `data-remove-variant-filter` : ""} class="tw-inline-flex tw-items-center tw-border tw-p-2 tw-px-3 tw-rounded-[4px] tw-border-scheme-border-secondary tw-gap-2 tw-cursor-pointer tw-mr-2 tw-mb-2">
          ${filter.key.includes("color") ? this.getSwatch(filter.value) : ""}
          <span class="tw-text-[12px] sm:tw-text-[13px]">${displayValue}</span>
          <img class="tw-size-4" src="${window.collectionIcons.close}">
        </button>
      `);
      this.selectedFiltersContainer.appendChild(dom);
    });
    const priceQueryParams = this.queryParams.filter((queryParam) => queryParam.key.includes("filter.ss_price"));
    priceQueryParams.forEach((priceQueryParam) => {
      const type = priceQueryParam.key.includes("high") ? "max" : "min";
      const matchingFilterValue = this.modules.doubleRangeSlider[`${type}Base`];
      if (priceQueryParam.value === matchingFilterValue) return;
      const dom = this.prepareStringForRender(`
        <button data-reset-price="${type}" class="tw-inline-flex tw-items-center tw-border tw-p-2 tw-rounded-[4px] tw-border-scheme-border-secondary tw-gap-2 tw-cursor-pointer tw-mr-2 tw-mb-2">
          <span class="tw-text-[12px] sm:tw-text-[13px]">Price ${type === "max" ? "<" : ">"}£${priceQueryParam.value}</span>
          <img class="tw-size-4" src="${window.collectionIcons.close}">
        </button>
      `);
      this.selectedFiltersContainer.appendChild(dom);
      totalFilters++;
    });
    const hierarchicalQueryParam = this.queryParams.find((queryParam) => queryParam.key.includes("full_path"));
    if (hierarchicalQueryParam) {
      const hierarchy = hierarchicalQueryParam.value.split(">");
      hierarchy.forEach((tag) => {
        const dom = this.prepareStringForRender(`
        <button data-remove-filter-hierarchical="${tag}" class="tw-inline-flex tw-items-center tw-border tw-p-2 tw-rounded-[4px] tw-border-scheme-border-secondary tw-gap-2 tw-cursor-pointer tw-mr-2 tw-mb-2">
          <span class="tw-text-[12px] sm:tw-text-[13px]">${tag}</span>
          <img class="tw-size-4" src="${window.collectionIcons.close}">
        </button>
      `);
        this.selectedFiltersContainer.appendChild(dom);
        totalFilters++;
      });
    }

    if (totalFilters) {
      const dom = this.prepareStringForRender(`
        <p class="tw-mb-2 tw-block sm:tw-inline-block sm:tw-mr-4 tw-text-[14px] sm:tw-text-[15px]">Selected filters</p>
      `);
      this.selectedFiltersContainer.prepend(dom);
    }

    if (totalFilters) {
      const dom = this.prepareStringForRender(`
        <div class="tw-inline-flex tw-underline tw-m-2 tw-mt-0 tw-cursor-pointer tw-text-[13px]" data-filters-are-applied data-clear-filters>Clear all</div>
      `);
      this.selectedFiltersContainer.appendChild(dom);
    }
  }

  getSwatch(value) {
    return `<div class="!tw-inline-block tw-size-2 tw-rounded-full" style="background: ${this.modules.swatch.getSwatch(value)};${value.toLowerCase() === "white" ? "border: 1px solid black" : `border: 1px solid ${this.modules.swatch.getSwatch(value)}`}"></div>`;
  }

  updateResetFiltersButton() {
    if (!this.resetFiltersButton) return;
    const activeFilters = this.querySelector("[data-filters-are-applied]");
    if (activeFilters) {
      this.resetFiltersButton.style.display = "block";
    } else {
      this.resetFiltersButton.style.display = "none";
    }
  }

  updateQuickFilters() {
    this.filterProxies.forEach((proxy) => {
      const matchingFilterLabel = proxy.dataset.filterProxyFor;
      const matchingFilter = this.filterElements.find((el) => el.dataset.filterLabel === matchingFilterLabel);
      if (!matchingFilter) {
        proxy.closest("[data-filter-proxy-wrapper]").style.display = "none";
        return;
      }
      proxy.closest("[data-filter-proxy-wrapper]").style.display = "block";
      proxy.checked = matchingFilter.checked;
    });
    const proxyContainer = this.querySelector("[data-filter-proxy-container]");
    if (proxyContainer) {
      if (
        this.filterProxies.length &&
        this.filterProxies.some((proxy) => proxy.closest("[data-filter-proxy-wrapper]").style.display === "block")
      ) {
        this.querySelector("[data-filter-proxy-container]").style.display = "flex";
      } else {
        this.querySelector("[data-filter-proxy-container]").style.display = "none";
      }
    }
  }

  enableLoadingState() {
    const loadingElement = this.prepareStringForRender(`
      <div data-loading-element class="!tw-flex tw-bg-scheme-background-blur tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-backdrop-blur-md tw-justify-center tw-align-top tw-pt-8">
        <img class="tw-animate-spin tw-size-12" src="${window.collectionIcons.spinner}" alt="Loading">
      </div>
    `);
    const paginationButton = this.paginationContainer?.querySelector("button");
    if (paginationButton) {
      const paginationLoader = this.prepareStringForRender(`
        <img class="tw-animate-spin tw-size-4" src="${window.collectionIcons.spinner}" alt="Loading">
      `);
      this.paginationContainer.querySelector("img")?.remove();
      paginationButton.disabled = true;
      paginationButton.appendChild(paginationLoader);
    }
    this.productContainer.appendChild(loadingElement);
    this.productContainer.classList.add("tw-min-h-40");
  }
  disableLoadingState() {
    const loadingElement = this.querySelector("[data-loading-element]");
    if (loadingElement) loadingElement.remove();
    this.productContainer.classList.remove("tw-min-h-40");
  }

  getVariantFilterList(filters) {
    const letterSizes = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
    const matchingFilter = filters.find((f) => f.field.includes("ss_variant_relational_filters_path"));
    if (!matchingFilter) {
      if (window.variantFiltersList) return window.variantFiltersList;
      return;
    }
    const sizeFilterObject = {
      ...matchingFilter,
      object: "variant",
      label: "Size",
      field: `filter.${matchingFilter.field}`,
      values: matchingFilter.values
        .filter((v) => !v.value.includes("size:*") && v.value.includes("instock:*|onsale:*"))
        .map((v) => {
          let label = v.value.split("|")[0].replace("size:", "");
          if (label.endsWith("UK")) label = `UK ${label.replace("UK", "").trim()}`;
          if (label.includes("UK")) label = `Z ${label}`;
          return { ...v, value: v.value.split("|")[0], label };
        })
        .sort((a, z) => {
          if (letterSizes.includes(a.label) && letterSizes.includes(z.label)) {
            return letterSizes.indexOf(a.label) - letterSizes.indexOf(z.label);
          } else if (letterSizes.includes(a.label)) {
            return -1;
          } else if (a.label.includes("Z UK") && z.label.includes("Z UK")) {
            const aLabel = parseFloat(a.label.replace("Z UK", "").trim());
            const zLabel = parseFloat(z.label.replace("Z UK", "").trim());
            return aLabel - zLabel;
          }
        })
        .map((v) => {
          return { ...v, label: v.label.replace("Z ", "") };
        }),
    };
    const inStockFilterObject = {
      ...matchingFilter,
      object: "variant",
      label: "In Stock",
      field: `filter.${matchingFilter.field}`,
      values: matchingFilter.values
        .filter((v) => !v.value.includes("instock:*") && v.value.includes("size:*") && v.value.includes("onsale:*"))
        .map((v) => {
          let label = v.value.split("|")[1].replace("instock:", "");
          return { ...v, value: v.value.split("|")[1], label };
        }),
    };
    const onSaleFilterObject = {
      ...matchingFilter,
      object: "variant",
      label: "On Sale",
      field: `filter.${matchingFilter.field}`,
      values: matchingFilter.values
        .filter((v) => !v.value.includes("onsale:*") && v.value.includes("size:*") && v.value.includes("instock:*"))
        .map((v) => {
          let label = v.value.split("|")[2].replace("onsale:", "");
          label = label === "yes" ? "On Sale" : "Not On Sale";
          return { ...v, value: v.value.split("|")[2], label };
        })
        .reverse(),
    };

    const variantFiltersList = [sizeFilterObject, onSaleFilterObject];
    window.variantFiltersList = variantFiltersList;
    return variantFiltersList;
  }

  getVariantFilterValue() {
    const tickedVariantFilters = this.variantFilters.filter((f) => f.checked);
    if (this.shouldSkipVariantFilters && !tickedVariantFilters.length) {
      this.variantFilterValueCache = [];
      return [];
    }
    const checkedCategories = tickedVariantFilters.map((f) => f.value.split(":"));

    const sizes = checkedCategories.filter(([key, value]) => key === "size");
    const onsales = checkedCategories.filter(([key, value]) => key === "onsale");
    if (!sizes.length) sizes.push(["size", "*"]);
    if (!onsales.length) onsales.push(["onsale", "*"]);
    const permutations = sizes.flatMap((s) => onsales.map((v) => [s, v]));

    const mappedValues = permutations.map((p) => {
      return {
        size: "*",
        instock: "yes",
        onsale: "*",
        ...Object.fromEntries(p),
      };
    });
    const filterValue = [
      ...mappedValues.map((m) => {
        return {
          key: "filter.ss_variant_relational_filters_path",
          value: Object.entries(m)
            .map(([key, value]) => `${key}:${value}`)
            .join("|"),
        };
      }),
    ];
    this.variantFilterValueCache = filterValue;
    return filterValue;
  }

  updateFilters(filters, ignoreDisplay) {
    if (!ignoreDisplay) {
      this.filterBar.style.display = "flex";
      this.selectedFiltersContainer.style.display = "block";
    }
    const variantFiltersList = this.getVariantFilterList(filters) || [];
    const dom = this.prepareStringForRender(`
      <div class="tw-flex tw-flex-col sm:tw-flex-row">
        ${[...filters, ...variantFiltersList]
          .map((f) => {
            if (!f) return;
            const { field, label, facet_active, collapse, hierarchy_delimiter, multiple, type, values, object } = f;
            if (object !== "variant" && !this.filtersToShow.includes(field)) return;
            const isColour = field.includes("color");
            const checkedFilters = this.checkedFilters.filter((f) => f.key === `filter.${field}`).length;
            return `
            <details data-details="${field}-${label}" data-filter-accordion class="tw-border-b tw-border-scheme-border-tertiary sm:tw-border-none tw-group">
              <summary class="tw-py-4 sm:tw-px-6 tw-uppercase tw-flex tw-justify-between tw-font-semibold tw-text-[14px] sm:tw-text-2xl tw-gap-2 tw-pr-0 sm:tw-pr-0">
                <div class="sm:tw-text-nowrap sm:tw-whitespace-nowrap">
                  ${label}
                  ${checkedFilters ? `<sup class="tw-sup tw-font-normal">[${checkedFilters}]</sup>` : ""}
                  ${type === "hierarchy" && this.queryParams.find(({ key, value }) => key.includes(field)) ? `<sup class="tw-sup tw-font-normal">[1]</sup>` : ""}
                </div>
                <div class="tw-block group-open:tw-hidden">
                  <img class="sm:tw-hidden" src="${window.collectionIcons.add}" alt="Open">
                  <img class="tw-hidden sm:tw-block tw-min-w-[21px]" src="${window.collectionIcons.sort}" alt="Open">
                </div>
                <div class="tw-hidden group-open:tw-block">
                  <img class="sm:tw-hidden" src="${window.collectionIcons.minus}" alt="Close">
                  <img class="tw-hidden sm:tw-block tw-rotate-180 tw-min-w-[21px]" src="${window.collectionIcons.sort}" alt="Close">
                </div>
              </summary>
              <ul class="sm:tw-absolute sm:tw-p-4 sm:tw-bg-scheme-background-blur sm:tw-rounded-[4px] sm:tw-shadow-shadow-3 tw-min-w-[280px] tw-w-[280px] sm:tw-backdrop-blur-lg sm:tw-z-999 tw-flex tw-flex-col tw-pb-4 tw-gap-4 sm:tw-max-h-[280px] sm:tw-overflow-y-auto sm:tw-ml-5">
                ${
                  type === "hierarchy" && this.queryParams.find(({ key, value }) => key.includes(field))
                    ? `
                  <li data-up-hierarchy="${field}" class="tw-underline tw-cursor-pointer">
                    Clear category filter
                  </li>
                `
                    : ""
                }
                ${!values.length ? `<li class="tw-text-[14px] sm:tw-text-2xl tw-text-[#444444]">Oops! There are no subcategories here. Try exploring another section</li>` : ""}
                ${values
                  .map((v) => {
                    const { active, count, label, type, value } = v;
                    return `
                    <li>
                      <label class="tw-cursor-pointer">
                        <input
                          class="filter-check tw-relative tw-invisible before:tw-size-[15px] before:tw-block before:tw-border before:tw-border-black before:tw-rounded-[2px] before:tw-visible checked:before:tw-bg-black tw-m-1 checked:after:tw-visible checked:after:tw-absolute checked:after:tw-size-full checked:after:tw-top-px checked:after:tw-left-px checked:after:tw-bg-center checked:after:tw-z-1 checked:after:tw-bg-no-repeat"
                          data-filter-field="${object !== "variant" ? "filter." : ""}${field}"
                          ${object === "variant" ? "data-variant-filter" : ""}
                          data-filter-label="${label}"
                          value="${value}"
                          type="checkbox"
                          ${object === "variant" ? (this.isVariantFilterActive(value) ? "checked" : "") : this.checkedFilters.find((f) => f.key === `${object !== "variant" ? "filter." : ""}${field}` && f.value === value) ? "checked" : ""}>
                        ${isColour ? this.getSwatch(value) : ""}
                        ${label}
                      </label>
                    </li>
                  `;
                  })
                  .join("")}
              </ul>
            </details>
          `;
          })
          .join("")}
          <p>
          </p>
      </div>
    `);
    this.filtersContainer.innerHTML = "";
    this.filtersContainer.appendChild(dom);
  }
  showEmptyState() {
    const isSearchEmptyState = this.queryParams && this.queryParams.find(({ key }) => key === "q");
    let searchString;
    if (isSearchEmptyState) searchString = this.queryParams.find(({ key }) => key === "q").value;
    if (isSearchEmptyState) {
      this.selectedFiltersContainer.style.display = "none";
      this.filterBar.style.display = "none";
    }
    let dom;
    if (!isSearchEmptyState) {
      dom = this.prepareStringForRender(`
        <div class="tw-p-4 tw-mx-auto sm:tw-text-center">
          <p>Oops, no products match your filters. Adjust your search or explore other options.</p>
          <button class="tw-bg-black tw-mt-4 tw-text-white tw-border tw-border-black tw-p-3 tw-text-[15px] tw-font-bold tw-text-xl tw-uppercase tw-rounded-[4px] tw-w-full sm:tw-w-auto" data-clear-filters>Reset filters</button>
        </div>
      `);
    } else {
      dom = this.prepareStringForRender(`
        <div class="tw-p-4 tw-mx-auto sm:tw-text-center">
          <p>Oops, no products matched "${searchString}". Please try a different keyword or check your spelling.</p>

        </div>
      `);
    }
    this.productContainer.innerHTML = "";
    this.emptyStateContainer.innerHTML = "";
    this.emptyStateContainer.appendChild(dom);
  }
  createBannerElement(bannerData) {
    const { id, imageUrl, targetUrl, title } = bannerData;

    const wrapper = document.createElement("div");
    wrapper.setAttribute("data-inline-banner", id);
    wrapper.className = "ss-inline-banner tw-relative tw-h-full";
    wrapper.style.setProperty("--banner-image", `url(${imageUrl})`);

    wrapper.innerHTML = `
    <a href="${targetUrl || "#"}" class="tw-relative tw-flex tw-flex-col tw-justify-between tw-items-center tw-h-full ss-inline-banner__link max-sm:tw-min-h-[321px] tw-group tw-overflow-hidden tw-rounded-[4px]">
      <div class="!tw-block ss-inline-banner__bg tw-absolute tw-inset-0 tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-105" style="background-image: url(${imageUrl}); background-size: cover; background-position: center;"></div>
      <div class="ss-inline-banner__overlay tw-absolute tw-inset-0 tw-pointer-events-none !tw-block"></div>
      <div class="tw-relative sm:tw-py-6 tw-py-3 tw-px-2">
        <p class="tw-m-0 tw-text-white h6 tw-text-center tw-uppercase">${title}</p>
      </div>
      <div class="tw-relative sm:tw-py-6 tw-py-3">
        <button
         class="tw-w-fit tw-inline-flex tw-justify-center tw-items-center tw-transition-all tw-duration-200 tw-ease-in-out tw-cursor-pointer tw-no-underline tw-border-0 tw-gap-2 btn-stylized !tw-font-bold tw-rounded-[4px] max-sm:tw-w-full sm:tw-max-h-[44px] tw-max-h-[40px] btn-flyup hover:tw-border lg:hover:!tw-shadow-none tw-border-white !tw-px-5 max-sm:!tw-p-3 max-sm:!tw-text-button-small" data-label="Shop Now" role="button" style="background:rgba(255, 255, 255, 0.2);">
          <span>View more</span>
        </button>
      </div>
    </a>
  `;

    return wrapper;
  }
  updateGrid(results, concat, inlineBanners = []) {
    if (!results.length) {
      this.showEmptyState();
      return;
    }
    this.emptyStateContainer.innerHTML = "";
    this.productCardComponent = "product-card";

    const banners = this.parseBannerData(inlineBanners);
    const bannerMap = new Map();
    banners.forEach((banner) => {
      bannerMap.set(banner.index, banner);
    });

    let productIndex = 0;
    let gridPosition = 0;

    if (concat) {
      productIndex = this.currentProductCount || 0;
      gridPosition = this.currentGridPosition || 0;
    } else {
      this.productContainer.innerHTML = "";
    }

    for (const product of results) {
      while (bannerMap.has(gridPosition)) {
        const bannerEl = this.createBannerElement(bannerMap.get(gridPosition));
        this.productContainer.appendChild(bannerEl);
        gridPosition++;
      }

      // Render product
      const card = document.createElement(this.productCardComponent);
      if (typeof card.setProduct === "function") {
        card.setProduct(product);
      } else {
        card.product = product;
      }
      card.setAttribute("data-quick-add-section", "main-collection-results");
      card.setAttribute("data-additional-image-padding", "true");
      this.productContainer.appendChild(card);

      productIndex++;
      gridPosition++;
    }

    while (bannerMap.has(gridPosition)) {
      const bannerEl = this.createBannerElement(bannerMap.get(gridPosition));
      this.productContainer.appendChild(bannerEl);
      gridPosition++;
    }

    this.currentProductCount = productIndex;
    this.currentGridPosition = gridPosition;
  }
  updateResultsCounters(pagination) {
    const { currentPage, perPage, totalPages, totalResults, begin, end, nextPage } = pagination;
    this.resultsCounterContainers.forEach((container) => {
      const dom = this.prepareStringForRender(`${totalResults}`);
      container.innerHTML = "";
      container.appendChild(dom);
    });
  }
  updatePagination(pagination) {
    const { currentPage, perPage, totalPages, totalResults, begin, end, nextPage } = pagination;
    if (!nextPage || currentPage === end) {
      this.paginationContainer.innerHTML = "";
      return;
    }
    const dom = this.prepareStringForRender(`
        <div class="tw-w-full tw-flex tw-py-3 tw-px-4 sm:tw-py-4 sm:tw-pt-0 tw-justify-center">
        <button data-load="${nextPage}" class="tw-flex tw-gap-2 tw-bg-transparent tw-text-black tw-border tw-border-black tw-p-3 sm:tw-px-5 tw-font-bold tw-text-[13px] sm:tw-text-2xl tw-uppercase tw-rounded-[4px] tw-items-center sm:hover:tw-text-[#3C3C3C] sm:hover:tw-border-[#3C3C3C] tw-group tw-h-[40px] sm:tw-h-[44px] tw-transition-all tw-duration-200 tw-ease-in-out tw-cursor-pointer btn-flyup" data-label="Show more">
          <span>
            Show more
          </span>
          <span class="svg-wrapper"><img class="sm:group-hover:tw-opacity-85" src="${window.collectionIcons.nextPage}" alt="Next page"></span>
      </button>
    </div>`);
    this.paginationContainer.innerHTML = "";
    this.paginationContainer.appendChild(dom);
  }
  updateDesktop() {
    this.filterAccordions.forEach((accordion) => {
      accordion.setAttribute("name", "filters");
    });
  }
  updateMobile() {
    this.filterAccordions.forEach((accordion) => {
      accordion.removeAttribute("name");
    });
  }
}

if (!customElements.get("searchspring-collection"))
  customElements.define("searchspring-collection", SearchspringCollection);
